var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import BigNumber from 'bignumber.js';
import { getIcon } from './getIcon';
import { getSuiClient } from './client';
import { getTxErrorMessage } from './txResult';
import { createAlphaFiSDK, validateNetwork } from './networkUtils';
import { webSocketService } from '../services/websocket';
import { getMoveErrorMessage, getRawErrorMessage, logTransactionError } from './errorMaps';
import { dryRunAndReport, dryRunDebug } from './dryRunDebug';
// Constants
const MIN_SUI_BALANCE = 0.5;
const SUI_DECIMALS = 9;
/**
 * Gas budget for money-path transactions, in MIST. 0.05 SUI.
 *
 * Sized against measured cost: the heaviest transaction on these paths runs ~21M MIST
 * (AlphaLend), with AlphaFi withdraw and stSUI both ~12M — so this is ~2.4x the worst case.
 * The bug it guards against was a ~2% shortfall, well inside estimate noise.
 *
 * Deliberately not larger. A budget is billed at actual usage, but it is *reserved* out of
 * the gas coin for the duration of the transaction, and SUI inputs come from that same
 * coin — so `amount + budget <= balance` must hold. An oversized budget silently breaks
 * near-max SUI deposits and stakes, which is the opposite failure from the one being fixed.
 *
 * The same constant and reasoning apply in alphalendTransactionHandler, stsuiTransactionHandler,
 * transferReceiptHandler and pendingWithdrawHandlers.
 *
 * Navi pools are the one exception and use NAVI_GAS_BUDGET_MIST below.
 */
const GAS_BUDGET_MIST = 50000000;
/**
 * Gas budget for pools whose parent protocol is Navi, in MIST. 0.1 SUI.
 *
 * Navi pools were observed reverting with InsufficientGas under the 50M budget above — their
 * deposit/withdraw path adds Navi's incentive-v3 reward claims to the strategy's own work.
 * Scoped to these pools so near-max SUI deposits everywhere else keep the tighter reservation.
 */
const NAVI_GAS_BUDGET_MIST = 100000000;
const EXPLORER_BASE_URL = 'https://suiscan.xyz';
/**
 * Pool parent protocols arrive from the backend's `parent_protocol_name` (e.g. ['Navi'],
 * ['Cetus']). Matched case-insensitively so a backend casing change cannot silently drop the
 * pool back to the smaller budget.
 *
 * Accepts a bare string too: the type says string[], but PoolsPage's icon lookup already
 * guards against the backend sending one protocol unwrapped, and this runs on the money path
 * where a `.some is not a function` would take down the whole transaction.
 */
const isNaviPool = (parentProtocols) => {
    if (!parentProtocols)
        return false;
    const protocols = Array.isArray(parentProtocols) ? parentProtocols : [parentProtocols];
    return protocols.some((protocol) => (protocol === null || protocol === void 0 ? void 0 : protocol.toLowerCase()) === 'navi');
};
// Helper functions
// `digest` lets BalanceContext read the transaction back and update wallet balances from
// its own amounts, instead of waiting for the next balance poll.
export const dispatchTransactionEvent = (txType, success, digest) => {
    const eventType = success ? 'transactionSuccess' : 'transactionError';
    document.dispatchEvent(new CustomEvent(eventType, { detail: { txType, success, digest } }));
};
const getNotificationTitle = (status, action) => {
    // Static titles
    if (status === 'awaiting')
        return 'Awaiting Response';
    if (status === 'insufficient_gas_balance')
        return 'Insufficient Gas Balance';
    // Action-based titles
    const titleMap = {
        success: {
            claim: 'Claim successful',
            deposit: 'Transaction Successful',
            withdraw: 'Transaction Successful',
            zap_deposit: 'Transaction Successful',
            claim_airdrop: 'Airdrop claim successful',
            stake: 'Transaction successful',
            unstake: 'Unstake Request Submitted',
            claim_reward: 'Rewards Claimed Successfully',
            claim_unstake: 'Transaction successful',
        },
        error: {
            deposit: 'Deposit Failed',
            withdraw: 'Withdraw Failed',
            claim: 'Claim Failed',
            zap_deposit: 'Transaction Failed',
            claim_airdrop: 'Airdrop claim failed',
            stake: 'Stake failed',
            unstake: 'Unstake failed',
            claim_reward: 'Rewards claim failed',
            claim_unstake: 'ALPHA claim failed',
        },
        cancelled: {
            deposit: 'Deposit Cancelled',
            withdraw: 'Withdraw Cancelled',
            claim: 'Claim Cancelled',
            zap_deposit: 'Transaction Cancelled',
            claim_airdrop: 'Airdrop claim cancelled',
            stake: 'Stake cancelled',
            unstake: 'Unstake request cancelled',
            claim_reward: 'Transaction cancelled',
            claim_unstake: 'Transaction cancelled',
        },
    };
    return titleMap[status][action];
};
const createExplorerLink = (network, digest) => {
    if (!digest)
        return '';
    const networkSuffix = network === 'testnet' ? 'testnet' : 'mainnet';
    return `${EXPLORER_BASE_URL}/${networkSuffix}/tx/${digest}`;
};
const formatPoolLocation = (action, poolName) => {
    if (!poolName)
        return '';
    const preposition = action === 'deposit' || action === 'zap_deposit' ? 'in' : 'from';
    return ` ${preposition} ${poolName} pool`;
};
const get5thSunday = (fromDate = new Date()) => {
    const date = new Date(fromDate);
    let sundaysFound = 0;
    // Move forward day-by-day until we find 5 Sundays
    while (sundaysFound < 5) {
        date.setDate(date.getDate() + 1);
        if (date.getDay() === 0) {
            // 0 = Sunday
            sundaysFound++;
        }
    }
    return date;
};
const getNotificationDescription = (status, action, displayAmount, poolName, errorMessage, supplyInMarket, repayInMarket) => {
    // Static messages
    if (status === 'awaiting')
        return 'Requesting wallet to approve transaction.';
    if (status === 'insufficient_gas_balance')
        return `At least ${MIN_SUI_BALANCE} SUI is required in your wallet to cover gas fees.`;
    // Action-based messages
    const poolLocation = formatPoolLocation(action, poolName);
    const date = get5thSunday(new Date());
    const unstakingDate = new Date(date.getTime()); //unstaking date is 5 sundays from now
    const formattedUnstakingDate = unstakingDate
        .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    })
        .replace(/,/g, '');
    // Special handling for claim_reward based on supply/repay options
    let claimRewardSuccessMessage = `${displayAmount} rewards were claimed successfully.`;
    if (action === 'claim_reward' && status === 'success') {
        if (supplyInMarket) {
            claimRewardSuccessMessage = `${displayAmount} claimed & supplied to SUI Market.`;
        }
        else if (repayInMarket) {
            claimRewardSuccessMessage = `${displayAmount} claimed & used to repay SUI Debt.`;
        }
        else {
            claimRewardSuccessMessage = `${displayAmount} claimed successfully in your wallet.`;
        }
    }
    const descriptionMap = {
        success: {
            claim: `Successfully claimed ${displayAmount} rewards and staked them to the ALPHA Locked Vault.`,
            deposit: `You have successfully deposited ${displayAmount}${poolLocation}.`,
            withdraw: `You have successfully withdrawn ${displayAmount}${poolLocation}.`,
            zap_deposit: `You have successfully swapped and deposited ${displayAmount}${poolLocation}.`,
            claim_airdrop: `You have successfully claimed airdrop rewards.`,
            stake: `${displayAmount} staked successfully`,
            unstake: `Your request to unstake ${displayAmount} has been submitted. It will be processed by ${formattedUnstakingDate}.`,
            claim_reward: claimRewardSuccessMessage,
            claim_unstake: `${displayAmount} claimed successfully.`,
        },
        error: {
            claim: `Failed to claim rewards. ${errorMessage}`,
            deposit: `Failed to deposit ${displayAmount}${poolLocation}. ${errorMessage}`,
            withdraw: `Failed to withdraw ${displayAmount}${poolLocation}. ${errorMessage}`,
            zap_deposit: `Failed to swap and deposit ${displayAmount}${poolLocation}. ${errorMessage}`,
            claim_airdrop: `Failed to claim airdrop rewards. ${errorMessage}`,
            stake: `Failed to stake ${displayAmount} ALPHA. ${errorMessage}`,
            unstake: `Failed to unstake ${displayAmount} ALPHA. ${errorMessage}`,
            claim_reward: `Failed to claim rewards. ${errorMessage}`,
            claim_unstake: `Failed to claim ${displayAmount} ALPHA. ${errorMessage}`,
        },
        cancelled: {
            claim: 'Claim rewards transaction was cancelled.',
            deposit: `Deposit of ${displayAmount}${poolLocation} was cancelled.`,
            withdraw: `Withdrawal of ${displayAmount}${poolLocation} was cancelled.`,
            zap_deposit: `Zap deposit of ${displayAmount}${poolLocation} was cancelled.`,
            claim_airdrop: `Airdrop claim transaction was cancelled.`,
            stake: `Staking of ${displayAmount} ALPHA was cancelled.`,
            unstake: `Unstaking of ${displayAmount} ALPHA was cancelled.`,
            claim_reward: `Failed to claim rewards. ${errorMessage}`,
            claim_unstake: `Failed to claim ${displayAmount} ALPHA. ${errorMessage}`,
        },
    };
    return descriptionMap[status][action];
};
const createNotificationDescription = (status, action, token, displayAmount, poolName, errorMessage, supplyInMarket, repayInMarket) => {
    // Static statuses don't need any parameters
    if (status === 'awaiting' || status === 'insufficient_gas_balance') {
        return getNotificationDescription(status);
    }
    // Handle cases with action, token, and displayAmount
    if (action && token && displayAmount) {
        return getNotificationDescription(status, action, displayAmount, poolName, errorMessage, supplyInMarket, repayInMarket);
    }
    // Fallback to default messages when no action/token/amount
    if (status === 'error')
        return errorMessage || '';
    return 'Transaction was cancelled.';
};
export const createNotificationInfo = (network, status, params = {}) => {
    const { result, action, token, displayAmount, poolName, errorMessage, supplyInMarket, repayInMarket, } = params;
    const explorerLink = createExplorerLink(network, result === null || result === void 0 ? void 0 : result.digest);
    const notificationConfigs = {
        awaiting: {
            title: getNotificationTitle('awaiting'),
            description: createNotificationDescription('awaiting'),
            spinner: true,
        },
        success: {
            title: getNotificationTitle('success', action),
            description: createNotificationDescription('success', action, token, displayAmount, poolName, undefined, supplyInMarket, repayInMarket),
            image: getIcon('success'),
            link: explorerLink,
        },
        error: {
            title: getNotificationTitle('error', action),
            description: action === 'deposit'
                ? `Failed to deposit ${displayAmount} in ${poolName} pool.${errorMessage ? ` ${errorMessage}` : ''}`
                : action === 'withdraw'
                    ? `Failed to withdraw ${displayAmount} from ${poolName} pool.${errorMessage ? ` ${errorMessage}` : ''}`
                    : action === 'zap_deposit'
                        ? `Failed to swap and deposit ${displayAmount} in ${poolName} pool.`
                        : action === 'claim_reward'
                            ? `Failed to claim rewards.`
                            : action === 'claim_unstake'
                                ? `Failed to claim unstake.`
                                : action === 'stake'
                                    ? `Failed to stake ${displayAmount} in ${poolName} pool.`
                                    : action === 'unstake'
                                        ? `Failed to unstake ${displayAmount} from ${poolName} pool.`
                                        : action === 'claim_airdrop'
                                            ? `Failed to claim airdrop.`
                                            : `Failed to claim rewards.`,
            image: getIcon('failed'),
            link: explorerLink,
        },
        cancelled: {
            title: getNotificationTitle('cancelled', action),
            description: createNotificationDescription('cancelled', action, token, displayAmount, poolName),
            image: getIcon('failed'),
        },
        insufficient_gas_balance: {
            title: getNotificationTitle('insufficient_gas_balance'),
            description: createNotificationDescription('insufficient_gas_balance'),
            image: getIcon('failed'),
        },
    };
    return notificationConfigs[status];
};
export const transactionHandler = (params) => __awaiter(void 0, void 0, void 0, function* () {
    const { amount, poolId, poolName, network, action, modalState, address, signAndExecuteTransaction, setNotificationInfo, setIsNotificationOpen, setIsTransactionLoading, displayAmount, suiBalance, isAmountA, withdrawMax, resetInputValues, decimals, ticketId, supplyInMarket, repayInMarket, slippage, coinType, parentProtocols, } = params;
    // Helper function to show notification and open modal
    const showNotification = (status, params = {}) => {
        // Add supplyInMarket and repayInMarket to params if available
        const notificationParams = Object.assign(Object.assign(Object.assign({}, params), (supplyInMarket !== undefined && { supplyInMarket })), (repayInMarket !== undefined && { repayInMarket }));
        setNotificationInfo(createNotificationInfo(network, status, notificationParams));
        setIsNotificationOpen(true);
    };
    // Helper function to handle error state
    const handleError = (rawError, shouldResetInputs = true, result, 
    // Stands in for the *message* when the caught value carries none, so callers can forward
    // the value itself unconditionally — a rejection payload with a `code` and no `message`
    // still reaches the log, where a stand-in string would have replaced it.
    fallbackMessage = '') => {
        const displayError = getMoveErrorMessage(getRawErrorMessage(rawError) || fallbackMessage);
        logTransactionError(action, rawError, displayError);
        showNotification('error', {
            errorMessage: displayError,
            action,
            token: modalState.token,
            displayAmount,
            poolName,
            result,
        });
        setIsTransactionLoading(false);
        if (shouldResetInputs)
            resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
        dispatchTransactionEvent(action.toLowerCase(), false);
    };
    // Early validation
    if (!address || (!poolId && action !== 'claim')) {
        console.error('Address and Pool ID are required for transactions (except claim)');
        return;
    }
    // Convert raw SUI balance to decimal format
    const suiBalanceDecimal = new BigNumber(suiBalance).dividedBy(new BigNumber(10).pow(SUI_DECIMALS));
    if (suiBalanceDecimal.lt(MIN_SUI_BALANCE)) {
        showNotification('insufficient_gas_balance');
        resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
        return;
    }
    const alphafiSdk = createAlphaFiSDK(network);
    const validatedNetwork = validateNetwork(network);
    const suiClient = getSuiClient(validatedNetwork);
    // Show awaiting notification
    showNotification('awaiting');
    setIsTransactionLoading(true);
    try {
        // Use BigNumber to handle decimal calculations properly
        const amountBigNumber = new BigNumber(amount).multipliedBy(new BigNumber(10).pow(parseInt(decimals)));
        const amountBigInt = BigInt(amountBigNumber.toFixed(0)); // Convert to integer string first
        let transactionBlock;
        switch (action) {
            case 'deposit':
                if (!poolId)
                    throw new Error('Pool ID is required for deposit');
                transactionBlock = yield alphafiSdk.deposit({
                    poolId,
                    amount: amountBigInt,
                    isAmountA,
                    address,
                    coinType,
                });
                break;
            case 'withdraw':
                if (!poolId)
                    throw new Error('Pool ID is required for withdraw');
                transactionBlock = yield alphafiSdk.withdraw({
                    poolId,
                    amount: amountBigInt.toString(),
                    isAmountA,
                    withdrawMax: withdrawMax || false,
                    address,
                    coinType,
                });
                break;
            case 'claim':
                transactionBlock = yield alphafiSdk.claim({
                    poolId: poolId, // Optional - if undefined, claims from all pools
                    address,
                });
                break;
            case 'claim_airdrop':
                transactionBlock = yield alphafiSdk.claimAirdrop({
                    address,
                    transferToWallet: true,
                });
                break;
            case 'stake':
                if (!poolId)
                    throw new Error('Pool ID is required for staking');
                transactionBlock = yield alphafiSdk.deposit({
                    poolId,
                    amount: amountBigInt,
                    isAmountA,
                    address,
                });
                break;
            case 'unstake':
                if (!poolId)
                    throw new Error('Pool ID is required for unstaking');
                transactionBlock = yield alphafiSdk.initiateWithdrawAlpha({
                    poolId,
                    amount: amountBigInt.toString(),
                    isAmountA,
                    withdrawMax: withdrawMax || false,
                    address,
                });
                break;
            case 'claim_unstake':
                if (!ticketId)
                    throw new Error('Ticket ID is required for claim unstaking');
                transactionBlock = yield alphafiSdk.claimWithdrawAlpha({
                    ticketId,
                    address,
                });
                break;
            case 'claim_reward': {
                const shouldTransferToWallet = !(supplyInMarket || repayInMarket);
                transactionBlock = yield alphafiSdk.claimAirdrop({
                    address,
                    transferToWallet: shouldTransferToWallet,
                });
                break;
            }
            case 'zap_deposit':
                if (!poolId)
                    throw new Error('Pool ID is required for zap deposit');
                transactionBlock = yield alphafiSdk.zapDepositTxb({
                    poolId,
                    inputCoinAmount: amountBigInt,
                    isInputA: isAmountA || false,
                    address,
                    slippage: slippage ? slippage / 100 : 0.01,
                });
                break;
            default:
                throw new Error(`Unsupported action: ${action}`);
        }
        if (!transactionBlock) {
            throw new Error('Failed to create transaction block');
        }
        // Pin an explicit gas budget instead of relying on the wallet's auto-estimate, which
        // has come in ~2% under the real cost on the heavy paths and reverted the transaction
        // on-chain with InsufficientGas. The dry run below cannot catch that either, because
        // isGasError() deliberately waves gas failures through for address-balance accounts.
        // Set before the dry run so the simulation runs under the same budget the wallet will.
        transactionBlock.setGasBudget(isNaviPool(parentProtocols) ? NAVI_GAS_BUDGET_MIST : GAS_BUDGET_MIST);
        // Simulate deposit/withdraw before prompting the wallet so on-chain
        // failures (e.g. flow_limiter) surface as a notification. In admin dry-run
        // mode every action is simulated and reported to the debug panel.
        if (action === 'deposit' || action === 'withdraw' || dryRunDebug.isEnabled()) {
            const dryRun = yield dryRunAndReport(transactionBlock, address, suiClient, action);
            // Admin dry-run mode: "awaiting" was shown before this; close it and let the
            // DryRunResultBox present the outcome. Never sign for an impersonated address.
            if (dryRunDebug.isEnabled()) {
                setIsNotificationOpen(false);
                setIsTransactionLoading(false);
                return;
            }
            if (!dryRun.success) {
                handleError(dryRun.rawError);
                return;
            }
        }
        signAndExecuteTransaction({ transaction: transactionBlock }, {
            onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    if (result.success) {
                        // Clear stale user-specific caches so the next transaction
                        // (e.g. withdraw after deposit) reads fresh on-chain state.
                        alphafiSdk.clearUserCaches(address);
                        showNotification('success', {
                            result,
                            action,
                            token: modalState.token,
                            displayAmount,
                            poolName,
                        });
                        resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
                        if (action === 'claim') {
                            webSocketService.sendClaimRewardsNotification(address);
                        }
                        else if (poolId) {
                            webSocketService.sendTransactionNotification(poolId, address);
                        }
                        dispatchTransactionEvent(action.toLowerCase(), true, result.digest);
                    }
                    else {
                        const txbCheck = yield suiClient
                            .waitForTransaction({ digest: result.digest, include: { effects: true } })
                            .catch(() => null);
                        const errorMessage = (txbCheck && getTxErrorMessage(txbCheck)) || 'Transaction failed';
                        handleError(errorMessage, true, result);
                    }
                }
                catch (error) {
                    console.error('Error waiting for transaction:', error);
                    handleError('Failed to confirm transaction', false, result);
                }
                finally {
                    setIsTransactionLoading(false);
                }
            }),
            onError: (error) => {
                // Hand over the error itself, not its message: handleError logs it, so the stack
                // (and anything else the wallet attached) survives.
                handleError(error, true, undefined, 'Transaction cancelled');
            },
        });
    }
    catch (error) {
        handleError(error);
    }
});
